import { Zap, FileCode, Palette, ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

export const SolutionSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: Palette,
      title: "AI Documentation in 30 Seconds",
      description:
        "Select your Figma frames, click generate. AI extracts spacing, colors, typography, and components automatically.",
      benefits: [
        "Auto-detect design specs",
        "Beautiful formatting",
        "Always up-to-date"
      ],
      gradient: "from-primary/20 to-primary/10",
      iconBg: "bg-primary",
    },
    {
      icon: FileCode,
      title: "Code-Ready Specs",
      description:
        "Automatic code snippets, responsive breakpoints, and asset exports. Everything developers need in one place.",
      benefits: [
        "CSS/Tailwind code",
        "Component structure",
        "Asset downloads"
      ],
      gradient: "from-primary/20 to-primary/10",
      iconBg: "bg-primary",
    },
    {
      icon: Zap,
      title: "10x Faster Handoffs",
      description:
        "What used to take hours now takes seconds. Spend your time designing, not documenting.",
      benefits: [
        "Instant generation",
        "Real-time updates",
        "One-click export"
      ],
      gradient: "from-primary/20 to-primary/10",
      iconBg: "bg-primary",
    },
  ];

  return (
    <section className="section-padding" id="features" ref={ref}>
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/20 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">AI-Powered Solution</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Your <span className="gradient-text">AI Design Assistant</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Transform Figma designs into perfect documentation with the power of AI
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="relative h-full glass-card rounded-2xl p-8 hover-lift transition-all duration-300">
                  {/* Gradient background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`} />
                  
                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl ${feature.iconBg} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold mb-4 group-hover:gradient-text transition-all duration-300">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      {feature.description}
                    </p>

                    {/* Benefits */}
                    <div className="space-y-3 mb-6">
                      {feature.benefits.map((benefit, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span className="text-sm text-foreground/80">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* Hover arrow */}
                    <div className="flex items-center gap-2 text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="text-sm">Learn more</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};